import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { NgSelectComponent } from '@ng-select/ng-select';
import { catchError, finalize, forkJoin, map, of, Subscription, switchMap } from 'rxjs';
import { DynamicFormService } from '../../core/services/dynamic-form.service';
import { PendingChangesAware } from '../../core/guards/pending-changes.guard';
import { SystemSettingsService } from '../../core/services/system-settings.service';
import { showAlertDialog } from '../../shared/helpers/showAlertDialog';
import { AlertType } from '../../shared/models/alertType';
import {
  InformationProcessingCardOptionColor,
  InformationProcessingCardResponse,
  InformationProcessingCardType,
  SaveInformationProcessingCardRequest,
  SaveInformationProcessingCardsRequest,
  SystemNotificationRule,
} from '../../shared/models/system-settings';
import { DynamicFormDetails, DynamicFormListItem } from '../formularios/models/dynamic-form';
import { CustomToggleComponent } from '../roteirizacao/components/custom-toggle/custom-toggle.component';
import { SectionCardComponent } from '../roteirizacao/components/section-card/section-card.component';
import { EditNotificationTemplateDialogComponent } from './dialogs/edit-notification-template-dialog.component';
import { ManageHolidaysDialogComponent } from './dialogs/manage-holidays-dialog.component';

interface NotificationRuleViewModel extends SystemNotificationRule {
  icon: string;
  resolvedDescription: string;
}

interface QuestionOption {
  id: number;
  text: string;
}

interface CardUiState {
  questionOptions: QuestionOption[];
  isLoadingQuestions: boolean;
  isExpanded: boolean;
}

const RULE_METADATA: Record<string, { icon: string; description: string }> = {
  ConsultantLateToRouteStart: {
    icon: 'schedule',
    description: 'Alerta quando o consultor não inicia a rota no horário programado',
  },
  ConsultantVisitDurationExceeded: {
    icon: 'hourglass_top',
    description: 'Notificação quando a visita ultrapassa o tempo estimado',
  },
  ConsultantRouteDeviation: {
    icon: 'route',
    description: 'Alerta quando há desvio significativo do trajeto planejado',
  },
  ConsultantVeryLateForStoreVisit: {
    icon: 'storefront',
    description: 'Notificação para atrasos significativos no cronograma',
  },
  PendingVisitsAtEndOfDay: {
    icon: 'event_busy',
    description: 'Alerta para visitas não realizadas próximo ao fim do expediente',
  },
  ConsultantTooLongAtSameStore: {
    icon: 'location_on',
    description: 'Alerta quando o consultor permanece tempo excessivo na mesma loja',
  },
  ConsultantMissingCheckInAfterPlannedTime: {
    icon: 'logout',
    description: 'Alerta para consultores que fizeram check-out de rota após o horário de execução da rota',
  },
};

const RULE_ORDER = [
  'ConsultantLateToRouteStart',
  'ConsultantVisitDurationExceeded',
  'ConsultantRouteDeviation',
  'ConsultantVeryLateForStoreVisit',
  'PendingVisitsAtEndOfDay',
  'ConsultantMissingCheckInAfterPlannedTime',
  'ConsultantTooLongAtSameStore',
];

const INFORMATION_TYPE_OPTIONS: Array<{ label: string; value: InformationProcessingCardType }> = [
  { label: 'Alternativas', value: 'Alternatives' },
  { label: 'Tags', value: 'Tags' },
  { label: 'Texto Livre', value: 'FreeText' },
];

const INFORMATION_COLOR_OPTIONS: Array<{
  label: string;
  value: InformationProcessingCardOptionColor;
  className: string;
}> = [
  { label: 'Cinza', value: 'Gray', className: 'bg-gray-200 text-gray-700' },
  { label: 'Verde', value: 'Green', className: 'bg-green-100 text-green-700' },
  { label: 'Azul', value: 'Blue', className: 'bg-blue-100 text-blue-700' },
  { label: 'Amarelo', value: 'Yellow', className: 'bg-yellow-100 text-yellow-700' },
  { label: 'Laranja', value: 'Orange', className: 'bg-orange-100 text-orange-700' },
  { label: 'Vermelho', value: 'Red', className: 'bg-red-100 text-red-700' },
];

@Component({
  selector: 'app-system-settings',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    NgSelectComponent,
    SectionCardComponent,
    CustomToggleComponent,
  ],
  templateUrl: './system-settings.component.html',
  styleUrl: './system-settings.component.scss',
})
export class SystemSettingsComponent implements OnInit, PendingChangesAware {
  readonly informationTypeOptions = INFORMATION_TYPE_OPTIONS;
  readonly informationColorOptions = INFORMATION_COLOR_OPTIONS;

  readonly form = this.formBuilder.group({
    processingCards: this.formBuilder.array([]),
  });

  notificationRules: NotificationRuleViewModel[] = [];
  formBaseOptions: DynamicFormListItem[] = [];
  cardUiState: Record<string, CardUiState> = {};

  isLoading = false;
  isSaving = false;
  hasDuplicateSavedCardIds = false;

  private cardSubscriptions = new Map<string, Subscription>();
  private formQuestionsCache = new Map<number, QuestionOption[]>();
  private nextCardId = 0;

  private initialRules = new Map<string, { isEnabled: boolean; template: string }>();
  private initialProcessingSnapshot = '[]';

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly settingsService: SystemSettingsService,
    private readonly dynamicFormService: DynamicFormService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadAllData();
  }

  get processingCardsArray(): FormArray {
    return this.form.get('processingCards') as FormArray;
  }

  hasPendingChanges(): boolean {
    const hasPendingRules = this.notificationRules.some((rule) => {
      const initial = this.initialRules.get(rule.type);
      if (!initial) {
        return false;
      }

      return initial.isEnabled !== rule.isEnabled || initial.template !== rule.template;
    });

    if (hasPendingRules) {
      return true;
    }

    return this.getCurrentProcessingSnapshot() !== this.initialProcessingSnapshot;
  }

  openEditTemplate(rule: NotificationRuleViewModel): void {
    this.dialog
      .open(EditNotificationTemplateDialogComponent, {
        data: {
          type: rule.type,
          name: rule.name,
          template: rule.template,
          saveFn: (payload: { type: string; template: string }) =>
            this.settingsService.updateNotificationTemplate(payload),
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result?: { updated?: boolean; template?: string }) => {
        const updatedTemplate = result?.template;
        if (!result?.updated || !updatedTemplate) {
          return;
        }

        this.notificationRules = this.notificationRules.map((item) =>
          item.type === rule.type
            ? {
                ...item,
                template: updatedTemplate,
              }
            : item,
        );
      });
  }

  onRuleToggle(type: string, value: boolean): void {
    this.notificationRules = this.notificationRules.map((rule) =>
      rule.type === type
        ? {
            ...rule,
            isEnabled: value,
          }
        : rule,
    );
  }

  openHolidayManagement(): void {
    this.dialog.open(ManageHolidaysDialogComponent, {
      disableClose: true,
    });
  }

  addInformationCard(): void {
    const cardForm = this.createProcessingCardForm();
    this.processingCardsArray.push(cardForm);
    this.syncCardExpandState(cardForm);
  }

  removeInformationCard(index: number, event: Event): void {
    event.stopPropagation();
    const card = this.processingCardsArray.at(index) as FormGroup;
    const savedId = Number(card.get('id')?.value);

    if (savedId > 0) {
      showAlertDialog(
        this.dialog,
        AlertType.Warning,
        'Excluir informação?',
        'Esta informação será removida do processamento da IA e do perfil dos clientes. Esta ação não pode ser desfeita.',
        'Excluir',
        'Cancelar',
        false,
        [],
        true,
      )
        .afterClosed()
        .subscribe((confirmed: boolean) => {
          if (!confirmed) {
            return;
          }

          this.settingsService.deleteInformationProcessingCard(savedId).subscribe({
            next: () => {
              this.loadAllData();
              showAlertDialog(this.dialog, AlertType.Success, 'Sucesso!', 'Informação excluída com sucesso.');
            },
          });
        });

      return;
    }

    this.removeCardAtIndex(index);
  }

  addOption(card: AbstractControl): void {
    const cardForm = this.asCardFormGroup(card);
    this.getCardOptionsArray(cardForm).push(this.createOptionForm());
    this.syncCardExpandState(cardForm);
    this.setCardExpandedState(cardForm, true);
  }

  removeOption(card: AbstractControl, optionIndex: number): void {
    const cardForm = this.asCardFormGroup(card);
    const optionsArray = this.getCardOptionsArray(cardForm);
    optionsArray.removeAt(optionIndex);
    this.syncCardExpandState(cardForm);
  }

  toggleCardExpand(card: AbstractControl): void {
    const cardForm = this.asCardFormGroup(card);
    const localId = this.getCardLocalId(cardForm);
    const current = this.cardUiState[localId];
    if (!current) {
      return;
    }

    this.cardUiState = {
      ...this.cardUiState,
      [localId]: {
        ...current,
        isExpanded: !current.isExpanded,
      },
    };
  }

  getCardQuestionOptions(card: AbstractControl): QuestionOption[] {
    return this.cardUiState[this.getCardLocalId(this.asCardFormGroup(card))]?.questionOptions ?? [];
  }

  isCardQuestionsLoading(card: AbstractControl): boolean {
    return this.cardUiState[this.getCardLocalId(this.asCardFormGroup(card))]?.isLoadingQuestions ?? false;
  }

  isCardExpanded(card: AbstractControl): boolean {
    return this.cardUiState[this.getCardLocalId(this.asCardFormGroup(card))]?.isExpanded ?? false;
  }

  shouldShowExpandToggle(card: AbstractControl): boolean {
    const cardForm = this.asCardFormGroup(card);
    return this.isAlternativeCard(cardForm) && this.getCardOptionsArray(cardForm).length > 1;
  }

  getVisibleOptionIndexes(card: AbstractControl): number[] {
    const cardForm = this.asCardFormGroup(card);
    const optionsLength = this.getCardOptionsArray(cardForm).length;
    if (optionsLength === 0) {
      return [];
    }

    return Array.from({ length: optionsLength }, (_, index) => index);
  }

  isAlternativeCard(card: AbstractControl): boolean {
    return this.asCardFormGroup(card).get('type')?.value === 'Alternatives';
  }

  getCardOptionsArray(card: AbstractControl): FormArray {
    return this.asCardFormGroup(card).get('options') as FormArray;
  }

  getColorOptionClass(color: InformationProcessingCardOptionColor | null | undefined): string {
    const match = this.informationColorOptions.find((option) => option.value === color);
    return match?.className ?? 'bg-gray-200 text-gray-700';
  }

  getColorOptionLabel(color: InformationProcessingCardOptionColor | null | undefined): string {
    const match = this.informationColorOptions.find((option) => option.value === color);
    return match?.label ?? 'Cinza';
  }

  getOptionControl(card: AbstractControl, optionIndex: number, field: 'label' | 'color'): AbstractControl | null {
    return this.getCardOptionsArray(card).at(optionIndex)?.get(field) ?? null;
  }

  isControlInvalid(control: AbstractControl | null): boolean {
    if (!control) {
      return false;
    }

    return control.invalid && (control.touched || control.dirty);
  }

  saveChanges(): void {
    const isProcessingValid = this.validateProcessingCards();

    if (this.form.invalid || !isProcessingValid) {
      this.form.markAllAsTouched();
      this.processingCardsArray.markAllAsTouched();
      showAlertDialog(this.dialog, AlertType.Warning, 'Campos obrigatórios', 'Preencha todos os campos obrigatórios antes de salvar.');
      return;
    }

    this.isSaving = true;
    const cardsPayload = this.buildProcessingPayload();

    forkJoin([
      this.settingsService.updateSettings({}),
      this.settingsService.updateNotificationRules({
        rules: this.notificationRules.map((rule) => ({
          type: rule.type,
          isEnabled: rule.isEnabled,
        })),
      }),
      this.settingsService.saveInformationProcessingCards(cardsPayload),
    ])
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.loadAllData();
          showAlertDialog(this.dialog, AlertType.Success, 'Sucesso!', 'Configurações atualizadas com sucesso.');
        },
      });
  }

  trackCard(_: number, control: AbstractControl): string {
    return String((control as FormGroup).get('localId')?.value ?? '');
  }

  trackByIndex(index: number): number {
    return index;
  }

  private loadAllData(): void {
    this.isLoading = true;

    forkJoin({
      settings: this.settingsService.getSettings(),
      forms: this.dynamicFormService.getList({
        page: 1,
        pageSize: 100,
        search: '',
      }),
      cards: this.settingsService.getInformationProcessingCards(),
    })
      .pipe(
        switchMap(({ settings, forms, cards }) => {
          this.applySettingsData(settings);
          this.formBaseOptions = [...forms.items].sort((a, b) => a.title.localeCompare(b.title));
          this.setProcessingCards(cards);

          const uniqueFormIds = Array.from(
            new Set(cards.map((card) => card.dynamicFormId).filter((formId) => Number(formId) > 0)),
          );

          if (uniqueFormIds.length === 0) {
            return of([] as Array<{ formId: number; questions: QuestionOption[] }>);
          }

          return forkJoin(
            uniqueFormIds.map((formId) =>
              this.loadQuestionsForForm(formId).pipe(
                map((questions) => ({ formId, questions })),
                catchError(() => of({ formId, questions: [] as QuestionOption[] })),
              ),
            ),
          );
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe({
        next: (questionBatch) => {
          const questionsMap = new Map(questionBatch.map((item) => [item.formId, item.questions]));

          this.processingCardsArray.controls.forEach((control) => {
            const card = control as FormGroup;
            const localId = this.getCardLocalId(card);
            const dynamicFormId = Number(card.get('dynamicFormId')?.value);
            if (dynamicFormId <= 0) {
              return;
            }

            const formQuestions = questionsMap.get(dynamicFormId);
            if (!formQuestions) {
              return;
            }

            this.updateCardQuestions(localId, formQuestions);
            this.filterCardSelectedQuestions(card, formQuestions);
          });

          this.saveAllSnapshots();
          this.form.markAsPristine();
        },
      });
  }

  private applySettingsData(response: { notificationRules: SystemNotificationRule[] }): void {
    this.notificationRules = response.notificationRules
      .map((rule) => {
        const metadata = RULE_METADATA[rule.type];
        return {
          ...rule,
          icon: metadata?.icon ?? 'notifications_active',
          resolvedDescription: rule.description?.trim() || metadata?.description || '',
        };
      })
      .sort((a, b) => {
        const aIndex = RULE_ORDER.indexOf(a.type);
        const bIndex = RULE_ORDER.indexOf(b.type);
        const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
        const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
        return normalizedA - normalizedB;
      });
  }

  private setProcessingCards(cards: InformationProcessingCardResponse[]): void {
    this.clearProcessingCards();
    this.formQuestionsCache.clear();

    cards
      .sort((a, b) => a.order - b.order)
      .forEach((card) => {
        const form = this.createProcessingCardForm(card);
        this.processingCardsArray.push(form);
        this.syncCardExpandState(form);
      });
  }

  private clearProcessingCards(): void {
    this.cardSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.cardSubscriptions.clear();
    this.cardUiState = {};
    this.processingCardsArray.clear();
  }

  private createProcessingCardForm(card?: InformationProcessingCardResponse): FormGroup {
    const optionsArray = this.formBuilder.array(
      (card?.options ?? [])
        .sort((a, b) => a.order - b.order)
        .map((option) => this.createOptionForm(option.label, option.color)),
    );

    const selectedQuestionIds = (card?.questions ?? []).map((item) => item.dynamicFormQuestionId);
    const localId = this.generateLocalId();
    const type = card?.type ?? 'Alternatives';

    const group = this.formBuilder.group({
      localId: [localId],
      id: [card?.id ?? null],
      title: [card?.title ?? '', [Validators.required, Validators.maxLength(200), this.trimmedRequiredValidator]],
      instruction: [card?.instruction ?? '', [Validators.required, Validators.maxLength(2000), this.trimmedRequiredValidator]],
      type: [type, [Validators.required]],
      dynamicFormId: [card?.dynamicFormId ?? null, [Validators.required, Validators.min(1)]],
      dynamicFormQuestionIds: [selectedQuestionIds, [this.arrayRequiredValidator()]],
      options: optionsArray,
    });

    const fallbackQuestions = (card?.questions ?? []).map((question) => ({
      id: question.dynamicFormQuestionId,
      text: question.questionText,
    }));

    this.cardUiState[localId] = {
      questionOptions: fallbackQuestions,
      isLoadingQuestions: false,
      isExpanded: false,
    };

    this.applyTypeConstraints(group, false);
    this.registerCardSubscriptions(group);

    return group;
  }

  private createOptionForm(
    label = '',
    color: InformationProcessingCardOptionColor = 'Gray',
  ): FormGroup {
    return this.formBuilder.group({
      label: [label, [Validators.required, Validators.maxLength(120), this.trimmedRequiredValidator]],
      color: [color, [Validators.required]],
    });
  }

  private registerCardSubscriptions(card: FormGroup): void {
    const localId = this.getCardLocalId(card);
    const subscription = new Subscription();

    const typeControl = card.get('type');
    if (typeControl) {
      subscription.add(
        typeControl.valueChanges.subscribe((type) => {
          this.onCardTypeChanged(card, type as InformationProcessingCardType);
        }),
      );
    }

    const formControl = card.get('dynamicFormId');
    if (formControl) {
      subscription.add(
        formControl.valueChanges.subscribe((formId) => {
          this.onCardFormChanged(card, Number(formId));
        }),
      );
    }

    this.cardSubscriptions.set(localId, subscription);
  }

  private onCardTypeChanged(card: FormGroup, type: InformationProcessingCardType): void {
    this.applyTypeConstraints(card, true);

    if (type === 'Alternatives') {
      return;
    }

    const optionsArray = this.getCardOptionsArray(card);
    if (optionsArray.length > 0) {
      optionsArray.clear();
    }
  }

  private onCardFormChanged(card: FormGroup, dynamicFormId: number): void {
    const selectedQuestionsControl = card.get('dynamicFormQuestionIds');
    selectedQuestionsControl?.setValue([], { emitEvent: false });

    const localId = this.getCardLocalId(card);
    if (!dynamicFormId || dynamicFormId <= 0) {
      this.updateCardQuestions(localId, []);
      return;
    }

    this.setCardQuestionsLoading(localId, true);

    this.loadQuestionsForForm(dynamicFormId)
      .pipe(finalize(() => this.setCardQuestionsLoading(localId, false)))
      .subscribe({
        next: (questions) => {
          this.updateCardQuestions(localId, questions);
        },
        error: () => {
          this.updateCardQuestions(localId, []);
        },
      });
  }

  private setCardQuestionsLoading(localId: string, isLoadingQuestions: boolean): void {
    const current = this.cardUiState[localId];
    if (!current) {
      return;
    }

    this.cardUiState = {
      ...this.cardUiState,
      [localId]: {
        ...current,
        isLoadingQuestions,
      },
    };
  }

  private updateCardQuestions(localId: string, questionOptions: QuestionOption[]): void {
    const current = this.cardUiState[localId];
    if (!current) {
      return;
    }

    this.cardUiState = {
      ...this.cardUiState,
      [localId]: {
        ...current,
        questionOptions,
      },
    };

    const card = this.findCardByLocalId(localId);
    if (card) {
      this.filterCardSelectedQuestions(card, questionOptions);
    }
  }

  private filterCardSelectedQuestions(card: FormGroup, questionOptions: QuestionOption[]): void {
    const selectedControl = card.get('dynamicFormQuestionIds');
    if (!selectedControl) {
      return;
    }

    const selected = this.toNumberArray(selectedControl.value);
    const validIds = new Set(questionOptions.map((item) => item.id));
    const filtered = selected.filter((id) => validIds.has(id));

    if (filtered.length !== selected.length) {
      selectedControl.setValue(filtered, { emitEvent: false });
    }
  }

  private loadQuestionsForForm(formId: number) {
    const cached = this.formQuestionsCache.get(formId);
    if (cached) {
      return of(cached);
    }

    return this.dynamicFormService.getFormDetails(formId).pipe(
      map((details) => this.extractQuestions(details)),
      map((questions) => {
        this.formQuestionsCache.set(formId, questions);
        return questions;
      }),
    );
  }

  private extractQuestions(details: DynamicFormDetails): QuestionOption[] {
    return details.sections
      .slice()
      .sort((a, b) => a.order - b.order)
      .flatMap((section) =>
        section.questions
          .slice()
          .sort((a, b) => a.order - b.order)
          .map((question) => ({
            id: question.id,
            text: question.text,
          })),
      );
  }

  private applyTypeConstraints(card: FormGroup, shouldUpdateTouchedState: boolean): void {
    const type = card.get('type')?.value as InformationProcessingCardType;
    const optionsArray = this.getCardOptionsArray(card);

    if (type === 'Alternatives' && optionsArray.length === 0) {
      optionsArray.push(this.createOptionForm());
    }

    if (type !== 'Alternatives' && optionsArray.length > 0) {
      optionsArray.clear();
      optionsArray.setErrors(null);
    }

    if (shouldUpdateTouchedState) {
      optionsArray.markAsTouched();
    }

    this.syncCardExpandState(card);
  }

  private syncCardExpandState(card: FormGroup): void {
    const localId = this.getCardLocalId(card);
    const current = this.cardUiState[localId];
    if (!current) {
      return;
    }

    const shouldCollapse = this.getCardOptionsArray(card).length <= 1;
    this.cardUiState = {
      ...this.cardUiState,
      [localId]: {
        ...current,
        isExpanded: shouldCollapse ? false : current.isExpanded,
      },
    };
  }

  private setCardExpandedState(card: FormGroup, isExpanded: boolean): void {
    const localId = this.getCardLocalId(card);
    const current = this.cardUiState[localId];
    if (!current) {
      return;
    }

    this.cardUiState = {
      ...this.cardUiState,
      [localId]: {
        ...current,
        isExpanded,
      },
    };
  }

  private validateProcessingCards(): boolean {
    let isValid = true;
    this.hasDuplicateSavedCardIds = false;

    const savedIdCounts = new Map<number, number>();

    this.processingCardsArray.controls.forEach((control) => {
      const card = control as FormGroup;
      const savedId = Number(card.get('id')?.value);
      if (savedId > 0) {
        savedIdCounts.set(savedId, (savedIdCounts.get(savedId) ?? 0) + 1);
      }
    });

    this.processingCardsArray.controls.forEach((control) => {
      const card = control as FormGroup;
      this.applyTypeConstraints(card, false);

      const savedId = Number(card.get('id')?.value);
      const hasDuplicatedSavedId = savedId > 0 && (savedIdCounts.get(savedId) ?? 0) > 1;
      this.updateControlError(card.get('id'), 'duplicatedCardId', hasDuplicatedSavedId);
      if (hasDuplicatedSavedId) {
        this.hasDuplicateSavedCardIds = true;
      }

      const selectedQuestionsControl = card.get('dynamicFormQuestionIds');
      const selectedQuestions = this.toNumberArray(selectedQuestionsControl?.value);
      const uniqueQuestions = new Set(selectedQuestions);

      this.updateControlError(selectedQuestionsControl, 'requiredArray', selectedQuestions.length === 0);
      this.updateControlError(selectedQuestionsControl, 'duplicatedQuestions', uniqueQuestions.size !== selectedQuestions.length);

      const optionsArray = this.getCardOptionsArray(card);
      const isAlternative = this.isAlternativeCard(card);

      if (isAlternative) {
        this.updateControlError(optionsArray, 'requiredOptions', optionsArray.length === 0);

        const normalizedLabels = optionsArray.controls
          .map((optionControl) => String(optionControl.get('label')?.value ?? '').trim().toLowerCase())
          .filter((label) => label.length > 0);

        this.updateControlError(
          optionsArray,
          'duplicatedLabels',
          new Set(normalizedLabels).size !== normalizedLabels.length,
        );
      } else {
        this.updateControlError(optionsArray, 'requiredOptions', false);
        this.updateControlError(optionsArray, 'duplicatedLabels', false);
      }

      card.updateValueAndValidity({ emitEvent: false });

      if (card.invalid) {
        isValid = false;
      }
    });

    return isValid;
  }

  private buildProcessingPayload(): SaveInformationProcessingCardsRequest {
    const cards: SaveInformationProcessingCardRequest[] = this.processingCardsArray.controls.map((control) => {
      const card = control as FormGroup;
      const idValue = Number(card.get('id')?.value);
      const type = card.get('type')?.value as InformationProcessingCardType;
      const options = this.getCardOptionsArray(card).controls.map((optionControl) => ({
        label: String(optionControl.get('label')?.value ?? '').trim(),
        color: optionControl.get('color')?.value as InformationProcessingCardOptionColor,
      }));

      return {
        id: idValue > 0 ? idValue : null,
        title: String(card.get('title')?.value ?? '').trim(),
        instruction: String(card.get('instruction')?.value ?? '').trim(),
        type,
        dynamicFormId: Number(card.get('dynamicFormId')?.value),
        dynamicFormQuestionIds: this.toNumberArray(card.get('dynamicFormQuestionIds')?.value),
        options: type === 'Alternatives' ? options : [],
      };
    });

    return { cards };
  }

  private saveAllSnapshots(): void {
    this.initialRules = new Map(
      this.notificationRules.map((rule) => [
        rule.type,
        {
          isEnabled: rule.isEnabled,
          template: rule.template,
        },
      ]),
    );

    this.updateProcessingSnapshot();
  }

  private updateProcessingSnapshot(): void {
    this.initialProcessingSnapshot = this.getCurrentProcessingSnapshot();
  }

  private getCurrentProcessingSnapshot(): string {
    const payload = this.buildProcessingPayload();
    const normalized = payload.cards.map((card) => ({
      ...card,
      dynamicFormQuestionIds: [...card.dynamicFormQuestionIds].sort((a, b) => a - b),
      options: card.options.map((option) => ({
        label: option.label,
        color: option.color,
      })),
    }));

    return JSON.stringify(normalized);
  }

  private removeCardAtIndex(index: number): void {
    const card = this.processingCardsArray.at(index) as FormGroup;
    const localId = this.getCardLocalId(card);

    this.processingCardsArray.removeAt(index);

    const subscription = this.cardSubscriptions.get(localId);
    if (subscription) {
      subscription.unsubscribe();
      this.cardSubscriptions.delete(localId);
    }

    const updatedState = { ...this.cardUiState };
    delete updatedState[localId];
    this.cardUiState = updatedState;
  }

  private findCardByLocalId(localId: string): FormGroup | null {
    for (const control of this.processingCardsArray.controls) {
      const card = control as FormGroup;
      if (this.getCardLocalId(card) === localId) {
        return card;
      }
    }

    return null;
  }

  private getCardLocalId(card: FormGroup): string {
    return String(card.get('localId')?.value ?? '');
  }

  private generateLocalId(): string {
    this.nextCardId += 1;
    return `processing-card-${this.nextCardId}`;
  }

  private updateControlError(control: AbstractControl | null, key: string, hasError: boolean): void {
    if (!control) {
      return;
    }

    const currentErrors = control.errors ?? {};
    if (hasError) {
      if (currentErrors[key]) {
        return;
      }

      control.setErrors({
        ...currentErrors,
        [key]: true,
      });
      return;
    }

    if (!currentErrors[key]) {
      return;
    }

    const { [key]: _, ...remainingErrors } = currentErrors;
    control.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
  }

  private arrayRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!Array.isArray(value) || value.length === 0) {
        return { requiredArray: true };
      }

      return null;
    };
  }

  private trimmedRequiredValidator(control: AbstractControl): ValidationErrors | null {
    const value = String(control.value ?? '').trim();
    return value.length > 0 ? null : { requiredTrimmed: true };
  }

  private toNumberArray(value: unknown): number[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map((item) => Number(item))
      .filter((item) => Number.isFinite(item) && item > 0);
  }

  private asCardFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }
}
